import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Zap,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Menu,
  X,
  Target
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Segurança Enterprise',
      description: 'Criptografia AES-256, headers OWASP e backup automático',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: '100% Anônimo',
      description: 'Proteção total da identidade do denunciante',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'LGPD Compliant',
      description: 'Sistema em conformidade com a Lei Geral de Proteção de Dados',
      color: 'from-violet-500 to-purple-600'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Interface Intuitiva',
      description: 'Dashboard simples e fácil de usar para gestão de denúncias',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const stats = [
    { value: '100%', label: 'LGPD Compliant' },
    { value: '0', label: 'Vulnerabilidades (Set/2025)' },
    { value: 'AES-256', label: 'Criptografia' },
    { value: '100%', label: 'Anônimo' }
  ];


  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                REPORTFius
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                Funcionalidades
              </a>
              <a href="#benefits" className="text-gray-700 hover:text-blue-600 font-medium transition">
                Benefícios
              </a>
              <button
                onClick={() => navigate('/seguranca')}
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                🛡️ Segurança
              </button>
              <button
                onClick={() => navigate('/login')}
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Acessar Sistema
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-6 space-y-4">
              <a href="#features" className="block text-gray-700 font-medium">Funcionalidades</a>
              <a href="#benefits" className="block text-gray-700 font-medium">Benefícios</a>
              <button onClick={() => navigate('/login')} className="block text-gray-700 font-medium">
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl"
              >
                Acessar Sistema
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-4 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
        
        {/* Animated Elements */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-40 left-10 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            {/* Security Badge */}
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full mb-8">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-sm font-semibold text-white">🛡️ Segurança Testada</span>
              <button
                onClick={() => navigate('/seguranca')}
                className="text-xs bg-green-500/20 text-green-300 px-3 py-1 rounded-full hover:bg-green-500/30 transition"
              >
                Ver Testes
              </button>
            </div>

            {/* Main Headline */}
            <h1 className="text-6xl md:text-8xl font-black mb-8 leading-tight">
              <span className="text-white">Canal de Denúncias</span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Inteligente e Seguro
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-2xl md:text-3xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
              <span className="font-semibold text-white">Gestão de Denúncias Corporativas</span>
              <br />
              <span className="text-xl text-blue-200">Sistema completo em conformidade com LGPD</span>
            </p>


            {/* Enhanced Trust Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div className="text-left">
                  <div className="text-white font-semibold">100% Anônimo</div>
                  <div className="text-blue-200 text-sm">Proteção total da identidade</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Shield className="w-6 h-6 text-blue-400" />
                <div className="text-left">
                  <div className="text-white font-semibold">Banco Criptografado</div>
                  <div className="text-blue-200 text-sm">AES-256 + Backup Seguro</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <Shield className="w-6 h-6 text-green-400" />
                <div className="text-left">
                  <div className="text-white font-semibold">100% Compliance</div>
                  <div className="text-blue-200 text-sm">LGPD + SOC 2 Ready</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-r from-slate-800 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjZmZmZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDMiPjxwYXRoIGQ9Ik0yMCAyMGMwLTExLjA0Ni04Ljk1NC0yMC0yMC0yMHYyMGgyMHoiLz48L2c+PC9zdmc+')]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nossos Números
            </h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
              Dados reais do sistema REPORTFius
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="group text-center bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="text-5xl md:text-6xl font-black text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text mb-3 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-blue-200 font-semibold text-lg">
                  {stat.label}
                </div>
                <div className="w-12 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto mt-4 rounded-full group-hover:w-16 transition-all duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')]"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Shield className="w-4 h-4" />
              <span>Tecnologia Avançada</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Funcionalidades
              </span>
              <br />
              <span className="text-gray-800">Essenciais</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Ferramentas essenciais para gestão eficiente e segura de denúncias corporativas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-white/20">
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                  {feature.description}
                </p>
                <div className="mt-6 w-full h-1 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Highlights Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')]"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Shield className="w-4 h-4" />
              <span>Segurança Testada</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Proteções</span> Implementadas
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Medidas de segurança reais testadas e validadas em produção
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Security Feature 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  🔐
                </div>
                <div className="ml-4">
                  <div className="text-white font-semibold">Criptografia AES-256</div>
                  <div className="text-blue-200 text-sm">Dados Protegidos</div>
                </div>
              </div>
              <p className="text-blue-100 leading-relaxed mb-4">
                Todos os dados sensíveis são criptografados usando AES-256-CBC com chaves rotacionadas automaticamente.
              </p>
            </div>

            {/* Security Feature 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  🛡️
                </div>
                <div className="ml-4">
                  <div className="text-white font-semibold">Headers de Segurança</div>
                  <div className="text-blue-200 text-sm">OWASP Compliant</div>
                </div>
              </div>
              <p className="text-blue-100 leading-relaxed mb-4">
                CSP, HSTS, X-Frame-Options e outros headers de segurança implementados conforme melhores práticas.
              </p>
            </div>

            {/* Security Feature 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  🔄
                </div>
                <div className="ml-4">
                  <div className="text-white font-semibold">Backup Automático</div>
                  <div className="text-blue-200 text-sm">Dados Seguros</div>
                </div>
              </div>
              <p className="text-blue-100 leading-relaxed mb-4">
                Backup automático criptografado do banco de dados com verificação de integridade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 px-4 bg-gradient-to-br from-white via-blue-50 to-indigo-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Target className="w-4 h-4" />
                <span>Vantagens Competitivas</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-8">
                Por que escolher o
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> REPORTFius?</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Gestão centralizada de denúncias
                    </h3>
                    <p className="text-gray-600">
                      Dashboard completo para acompanhamento e análise de casos
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Relatórios detalhados
                    </h3>
                    <p className="text-gray-600">
                      Analytics completos para tomada de decisão estratégica
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Suporte jurídico especializado FIUS
                    </h3>
                    <p className="text-gray-600">
                      Time de especialistas em compliance à sua disposição
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="mt-8 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
              >
                Acessar Sistema
              </button>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800"
                alt="Dashboard"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">+280%</p>
                    <p className="text-sm text-gray-600">Eficiência</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-32 px-4 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')]"></div>
        
        {/* Animated Elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full mb-8">
            <Target className="w-5 h-5 text-yellow-400" />
            <span className="text-sm font-semibold text-white">🎯 Comece Agora</span>
          </div>
          
          <h2 className="text-6xl md:text-7xl font-black text-white mb-8 leading-tight">
            Pronto para <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">revolucionar</span>
            <br />
            seu canal de denúncias?
          </h2>
          
          <p className="text-2xl text-blue-200 mb-12 max-w-4xl mx-auto leading-relaxed">
            Sistema completo de gestão de denúncias com 
            <span className="font-bold text-white"> segurança enterprise</span> e 
            <span className="font-bold text-yellow-400"> conformidade LGPD</span>
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-8 mb-16">
            <button
              onClick={() => navigate('/register')}
              className="group px-12 py-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold text-xl rounded-2xl hover:shadow-2xl hover:shadow-blue-500/25 transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
            >
              <span>🚀 Acessar Sistema</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/seguranca')}
              className="group px-12 py-6 bg-white/10 backdrop-blur-sm text-white font-bold text-xl rounded-2xl border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300 flex items-center space-x-3"
            >
              <span>🛡️ Ver Segurança</span>
            </button>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-3 text-blue-200">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <span className="font-semibold">Sem compromisso</span>
            </div>
            <div className="flex items-center justify-center space-x-3 text-blue-200">
              <Shield className="w-6 h-6 text-blue-400" />
              <span className="font-semibold">100% Seguro</span>
            </div>
            <div className="flex items-center justify-center space-x-3 text-blue-200">
              <Shield className="w-6 h-6 text-green-400" />
              <span className="font-semibold">100% Anônimo</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-gray-900 to-blue-900 text-gray-400 py-16 px-4 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')]"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <span className="text-2xl font-black text-white">REPORTFius</span>
              </div>
              <p className="text-lg text-gray-300 mb-6 max-w-md leading-relaxed">
                Sistema completo de gestão de denúncias corporativas. 
                <span className="text-white font-semibold">Conformidade com LGPD.</span>
              </p>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/seguranca')}
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-all duration-300"
                >
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-semibold">Ver Segurança</span>
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300"
                >
                  <span className="text-sm font-semibold">Acessar Sistema</span>
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-6">Produto</h3>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors duration-300">Funcionalidades</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Integrações</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">API</a></li>
                <li><a href="/seguranca" className="text-gray-400 hover:text-white transition-colors duration-300">Segurança</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-6">Empresa</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Sobre Nós</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Carreiras</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Contato</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Imprensa</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-center md:text-left mb-4 md:mb-0">
                <p className="text-gray-400">
                  © 2025 REPORTFius. Todos os direitos reservados.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Feito com ❤️ no Brasil
                </p>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Termos de Uso</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacidade</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookies</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;